import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Row, Col, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { createMilestone, updateMilestone } from "../Api/MilestoneMaster";
import { getTasksByProject } from "../Api/TaskMaster";

const MilestoneModal = ({
  show,
  onHide,
  milestone,
  projectId,
  onMilestoneSaved,
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;
  const userName = userDetails?.username || "System";

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    percentage: "",
    plannedStartDate: "",
    plannedEndDate: "",
    actualStartDate: "",
    actualEndDate: "",
    prozmaPrId: projectId || "",
    status: "",
    description: "",
    orgId: String(orgId) || "",
    taskAssigned: [], // Initialize as empty array
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  // Milestone type options
  const typeOptions = [
    { value: "", label: "Select Type" },
    { value: "Planning", label: "Planning" },
    { value: "Development", label: "Development" },
    { value: "Testing", label: "Testing" },
    { value: "Deployment", label: "Deployment" },
    { value: "Review", label: "Review" },
    { value: "Approval", label: "Approval" },
    { value: "Delivery", label: "Delivery" },
    { value: "Other", label: "Other" },
  ];

  // Status options
  const statusOptions = [
    { value: "", label: "Select Status" },
    { value: "Not Started", label: "Not Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "On Hold", label: "On Hold" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Delayed", label: "Delayed" },
  ];

  // Fetch tasks when modal opens and projectId is available
  useEffect(() => {
    if (show && projectId) {
      fetchTasks();
    }
  }, [show, projectId]);

  useEffect(() => {
    if (milestone) {
      // Edit mode - format dates for input fields and handle taskAssigned
      const taskAssignedArray = milestone.taskAssigned
        ? Array.isArray(milestone.taskAssigned)
          ? milestone.taskAssigned
          : milestone.taskAssigned
              .split(",")
              .map((task) => task.trim())
              .filter((task) => task)
        : [];

      setFormData({
        name: milestone.name || "",
        type: milestone.type || "",
        percentage: milestone.percentage || "",
        plannedStartDate: milestone.plannedStartDate
          ? new Date(milestone.plannedStartDate).toISOString().split("T")[0]
          : "",
        plannedEndDate: milestone.plannedEndDate
          ? new Date(milestone.plannedEndDate).toISOString().split("T")[0]
          : "",
        actualStartDate: milestone.actualStartDate
          ? new Date(milestone.actualStartDate).toISOString().split("T")[0]
          : "",
        actualEndDate: milestone.actualEndDate
          ? new Date(milestone.actualEndDate).toISOString().split("T")[0]
          : "",
        prozmaPrId: milestone.prozmaPrId || projectId || "",
        status: milestone.status || "",
        description: milestone.description || "",
        orgId: milestone.orgId || String(orgId) || "",
        taskAssigned: taskAssignedArray,
      });

      // Initialize selected task IDs
      setSelectedTaskIds(new Set(taskAssignedArray));
    } else {
      // Create mode
      setFormData({
        name: "",
        type: "",
        percentage: "",
        plannedStartDate: "",
        plannedEndDate: "",
        actualStartDate: "",
        actualEndDate: "",
        prozmaPrId: projectId || "",
        status: "",
        description: "",
        orgId: String(orgId) || "",
        taskAssigned: [],
      });
      setSelectedTaskIds(new Set());
    }
    setErrors({});
  }, [milestone, show, projectId, orgId]);

  // Fetch tasks for the project
  const fetchTasks = async () => {
    if (!projectId) return;

    setLoadingTasks(true);
    try {
      const tasksData = await getTasksByProject(projectId);
      console.log("Fetched tasks:", tasksData);
      setTasks(tasksData || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Milestone name is required";
    }

    if (!formData.type) {
      newErrors.type = "Milestone type is required";
    }

    if (!formData.prozmaPrId) {
      newErrors.prozmaPrId = "Project ID is required";
    }

    if (!formData.orgId) {
      newErrors.orgId = "Organization ID is required";
    }

    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    if (formData.percentage) {
      const percentageValue = parseFloat(formData.percentage);
      if (
        isNaN(percentageValue) ||
        percentageValue < 0 ||
        percentageValue > 100
      ) {
        newErrors.percentage = "Percentage must be a number between 0 and 100";
      }
    }

    // Date validation
    if (formData.plannedStartDate && formData.plannedEndDate) {
      const startDate = new Date(formData.plannedStartDate);
      const endDate = new Date(formData.plannedEndDate);
      if (startDate > endDate) {
        newErrors.plannedEndDate =
          "Planned end date cannot be before start date";
      }
    }

    if (formData.actualStartDate && formData.actualEndDate) {
      const startDate = new Date(formData.actualStartDate);
      const endDate = new Date(formData.actualEndDate);
      if (startDate > endDate) {
        newErrors.actualEndDate = "Actual end date cannot be before start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare data for API
      const submitData = {
        name: formData.name,
        type: formData.type,
        percentage: formData.percentage,
        plannedStartDate: formData.plannedStartDate || null,
        plannedEndDate: formData.plannedEndDate || null,
        actualStartDate: formData.actualStartDate || null,
        actualEndDate: formData.actualEndDate || null,
        prozmaPrId: formData.prozmaPrId,
        status: formData.status,
        description: formData.description,
        orgId: formData.orgId,
        taskAssigned:
          formData.taskAssigned.length > 0 ? formData.taskAssigned : null,
      };

      if (milestone) {
        // Update existing milestone
        const updateData = {
          ...submitData,
          milestoneMavroId: milestone.milestoneMavroId,
          milestonesId: milestone.milestonesId,
        };
        await updateMilestone(updateData, userName);
        toast.success("Milestone updated successfully");
      } else {
        // Create new milestone
        await createMilestone(submitData, userName);
        toast.success("Milestone created successfully");
      }

      onMilestoneSaved();
      onHide();
    } catch (error) {
      console.error("Error saving milestone:", error);
      toast.error(error.message || "Failed to save milestone");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle task checkbox change
  const handleTaskCheckboxChange = (taskId) => {
    const newSelectedTaskIds = new Set(selectedTaskIds);

    if (newSelectedTaskIds.has(taskId)) {
      newSelectedTaskIds.delete(taskId);
    } else {
      newSelectedTaskIds.add(taskId);
    }

    setSelectedTaskIds(newSelectedTaskIds);
  };

  // Apply selected tasks and close dropdown
  const handleApplyTasks = () => {
    const selectedTasksArray = Array.from(selectedTaskIds);
    handleChange("taskAssigned", selectedTasksArray);
    setShowTaskDropdown(false);
  };

  // Get task name by ID
  const getTaskNameById = (taskId) => {
    const task = tasks.find((t) => t.taskId === taskId);
    return task ? task.taskName : `Task ${taskId}`;
  };

  // Remove a selected task
  const removeSelectedTask = (taskId) => {
    const newSelectedTaskIds = new Set(selectedTaskIds);
    newSelectedTaskIds.delete(taskId);
    setSelectedTaskIds(newSelectedTaskIds);

    const selectedTasksArray = Array.from(newSelectedTaskIds);
    handleChange("taskAssigned", selectedTasksArray);
  };

  const formatPercentage = (value) => {
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    return numericValue;
  };

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {milestone ? "Edit Milestone" : "Add New Milestone"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.general && <Alert variant="danger">{errors.general}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Milestone Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter milestone name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  isInvalid={!!errors.type}
                >
                  {typeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.type}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {/* <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Percentage (%)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.percentage}
                  onChange={(e) =>
                    handleChange("percentage", formatPercentage(e.target.value))
                  }
                  isInvalid={!!errors.percentage}
                  placeholder="Enter completion percentage (0-100)"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.percentage}
                </Form.Control.Feedback>
              </Form.Group>
            </Col> */}

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status *</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  isInvalid={!!errors.status}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.status}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Redesigned Task Assigned Field */}
          <Form.Group className="mb-3">
            <Form.Label>Assigned Tasks</Form.Label>

            {/* Selected Tasks Display */}
            <div className="mb-2">
              {formData.taskAssigned.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {formData.taskAssigned.map((taskId) => (
                    <Badge
                      key={taskId}
                      bg="primary"
                      className="d-flex align-items-center"
                      style={{ fontSize: "0.875rem", padding: "0.5rem" }}
                    >
                      {getTaskNameById(taskId)} ({taskId})
                      <Button
                        variant="link"
                        className="text-white p-0 ms-2"
                        style={{ textDecoration: "none" }}
                        onClick={() => removeSelectedTask(taskId)}
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-muted">No tasks selected</div>
              )}
            </div>

            {/* Task Selection Dropdown */}
            <div className="position-relative">
              <Button
                variant="outline-secondary"
                onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                disabled={loadingTasks || tasks.length === 0}
                className="w-100 text-start"
              >
                {loadingTasks ? "Loading tasks..." : "Select Tasks"}
              </Button>

              {showTaskDropdown && (
                <div
                  className="position-absolute top-100 start-0 end-0 bg-white border rounded shadow-lg z-3 mt-1"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {tasks.length > 0 ? (
                    <>
                      <div className="p-2 border-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Select tasks to assign ({selectedTaskIds.size}{" "}
                            selected)
                          </small>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleApplyTasks}
                          >
                            OK
                          </Button>
                        </div>
                      </div>
                      <div className="p-2">
                        {tasks.map((task) => (
                          <Form.Check
                            key={task.taskId}
                            type="checkbox"
                            id={`task-${task.taskId}`}
                            label={`${task.taskName} (${task.taskId})`}
                            checked={selectedTaskIds.has(task.taskId)}
                            onChange={() =>
                              handleTaskCheckboxChange(task.taskId)
                            }
                            className="mb-2"
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="p-3 text-center text-muted">
                      No tasks available for this project
                    </div>
                  )}
                </div>
              )}
            </div>

            <Form.Text className="text-muted">
              Click "Select Tasks" to choose tasks for this milestone. Selected
              tasks will be displayed above.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Planned Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.plannedStartDate}
                  onChange={(e) =>
                    handleChange("plannedStartDate", e.target.value)
                  }
                  max={formData.plannedEndDate || getTodayDate()}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Planned End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.plannedEndDate}
                  onChange={(e) =>
                    handleChange("plannedEndDate", e.target.value)
                  }
                  min={formData.plannedStartDate}
                  isInvalid={!!errors.plannedEndDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.plannedEndDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Actual Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.actualStartDate}
                  onChange={(e) =>
                    handleChange("actualStartDate", e.target.value)
                  }
                  max={formData.actualEndDate || getTodayDate()}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Actual End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.actualEndDate}
                  onChange={(e) =>
                    handleChange("actualEndDate", e.target.value)
                  }
                  min={formData.actualStartDate}
                  isInvalid={!!errors.actualEndDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.actualEndDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter milestone description"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Project ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.prozmaPrId}
                  onChange={(e) => handleChange("prozmaPrId", e.target.value)}
                  isInvalid={!!errors.prozmaPrId}
                  disabled={!!projectId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.prozmaPrId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.orgId}
                  onChange={(e) => handleChange("orgId", e.target.value)}
                  isInvalid={!!errors.orgId}
                  disabled={!!orgId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.orgId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading
              ? "Saving..."
              : milestone
              ? "Update Milestone"
              : "Create Milestone"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MilestoneModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { createMilestone, updateMilestone } from "../Api/MilestoneMaster";

// const MilestoneModal = ({
//   show,
//   onHide,
//   milestone,
//   projectId,
//   onMilestoneSaved,
// }) => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;
//   const userName = userDetails?.username || "System";

//   const [formData, setFormData] = useState({
//     name: "",
//     type: "",
//     percentage: "",
//     plannedStartDate: "",
//     plannedEndDate: "",
//     actualStartDate: "",
//     actualEndDate: "",
//     prozmaPrId: projectId || "",
//     status: "",
//     description: "",
//     orgId: String(orgId) || "",
//   });

//   console.log(formData, "formData");

//   const [loading, setLoading] = useState(false);
//   const [errors, setErrors] = useState({});

//   // Milestone type options
//   const typeOptions = [
//     { value: "", label: "Select Type" },
//     { value: "Planning", label: "Planning" },
//     { value: "Development", label: "Development" },
//     { value: "Testing", label: "Testing" },
//     { value: "Deployment", label: "Deployment" },
//     { value: "Review", label: "Review" },
//     { value: "Approval", label: "Approval" },
//     { value: "Delivery", label: "Delivery" },
//     { value: "Other", label: "Other" },
//   ];

//   // Status options
//   const statusOptions = [
//     { value: "", label: "Select Status" },
//     { value: "Not Started", label: "Not Started" },
//     { value: "In Progress", label: "In Progress" },
//     { value: "Completed", label: "Completed" },
//     { value: "On Hold", label: "On Hold" },
//     { value: "Cancelled", label: "Cancelled" },
//     { value: "Delayed", label: "Delayed" },
//   ];

//   useEffect(() => {
//     if (milestone) {
//       // Edit mode - format dates for input fields
//       setFormData({
//         name: milestone.name || "",
//         type: milestone.type || "",
//         percentage: milestone.percentage || "",
//         plannedStartDate: milestone.plannedStartDate
//           ? new Date(milestone.plannedStartDate).toISOString().split("T")[0]
//           : "",
//         plannedEndDate: milestone.plannedEndDate
//           ? new Date(milestone.plannedEndDate).toISOString().split("T")[0]
//           : "",
//         actualStartDate: milestone.actualStartDate
//           ? new Date(milestone.actualStartDate).toISOString().split("T")[0]
//           : "",
//         actualEndDate: milestone.actualEndDate
//           ? new Date(milestone.actualEndDate).toISOString().split("T")[0]
//           : "",
//         prozmaPrId: milestone.prozmaPrId || projectId || "",
//         status: milestone.status || "",
//         description: milestone.description || "",
//         orgId: milestone.orgId || String(orgId) || "",
//       });
//     } else {
//       // Create mode
//       setFormData({
//         name: "",
//         type: "",
//         percentage: "",
//         plannedStartDate: "",
//         plannedEndDate: "",
//         actualStartDate: "",
//         actualEndDate: "",
//         prozmaPrId: projectId || "",
//         status: "",
//         description: "",
//         orgId: String(orgId) || "",
//       });
//     }
//     setErrors({});
//   }, [milestone, show, projectId, orgId]);

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.name.trim()) {
//       newErrors.name = "Milestone name is required";
//     }

//     if (!formData.type) {
//       newErrors.type = "Milestone type is required";
//     }

//     if (!formData.prozmaPrId) {
//       newErrors.prozmaPrId = "Project ID is required";
//     }

//     if (!formData.orgId) {
//       newErrors.orgId = "Organization ID is required";
//     }

//     if (!formData.status) {
//       newErrors.status = "Status is required";
//     }

//     if (formData.percentage) {
//       const percentageValue = parseFloat(formData.percentage);
//       if (
//         isNaN(percentageValue) ||
//         percentageValue < 0 ||
//         percentageValue > 100
//       ) {
//         newErrors.percentage = "Percentage must be a number between 0 and 100";
//       }
//     }

//     // Date validation
//     if (formData.plannedStartDate && formData.plannedEndDate) {
//       const startDate = new Date(formData.plannedStartDate);
//       const endDate = new Date(formData.plannedEndDate);
//       if (startDate > endDate) {
//         newErrors.plannedEndDate =
//           "Planned end date cannot be before start date";
//       }
//     }

//     if (formData.actualStartDate && formData.actualEndDate) {
//       const startDate = new Date(formData.actualStartDate);
//       const endDate = new Date(formData.actualEndDate);
//       if (startDate > endDate) {
//         newErrors.actualEndDate = "Actual end date cannot be before start date";
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     try {
//       // Prepare data for API
//       const submitData = {
//         name: formData.name,
//         type: formData.type,
//         percentage: formData.percentage,
//         plannedStartDate: formData.plannedStartDate || null,
//         plannedEndDate: formData.plannedEndDate || null,
//         actualStartDate: formData.actualStartDate || null,
//         actualEndDate: formData.actualEndDate || null,
//         prozmaPrId: formData.prozmaPrId,
//         status: formData.status,
//         description: formData.description,
//         orgId: formData.orgId,
//       };

//       if (milestone) {
//         // Update existing milestone
//         const updateData = {
//           ...submitData,
//           milestoneMavroId: milestone.milestoneMavroId,
//           milestonesId: milestone.milestonesId,
//         };
//         await updateMilestone(updateData, userName);
//         toast.success("Milestone updated successfully");
//       } else {
//         // Create new milestone
//         await createMilestone(submitData, userId);
//         toast.success("Milestone created successfully");
//       }

//       onMilestoneSaved();
//       onHide();
//     } catch (error) {
//       console.error("Error saving milestone:", error);
//       toast.error(error.message || "Failed to save milestone");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (field, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));

//     // Clear error when user starts typing
//     if (errors[field]) {
//       setErrors((prev) => ({
//         ...prev,
//         [field]: "",
//       }));
//     }
//   };

//   const formatPercentage = (value) => {
//     // Remove any non-numeric characters except decimal point
//     const numericValue = value.replace(/[^\d.]/g, "");
//     return numericValue;
//   };

//   const getTodayDate = () => {
//     return new Date().toISOString().split("T")[0];
//   };

//   return (
//     <Modal show={show} onHide={onHide} size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>
//           {milestone ? "Edit Milestone" : "Add New Milestone"}
//         </Modal.Title>
//       </Modal.Header>

//       <Form onSubmit={handleSubmit}>
//         <Modal.Body>
//           {errors.general && <Alert variant="danger">{errors.general}</Alert>}

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Milestone Name *</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.name}
//                   onChange={(e) => handleChange("name", e.target.value)}
//                   isInvalid={!!errors.name}
//                   placeholder="Enter milestone name"
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.name}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Type *</Form.Label>
//                 <Form.Select
//                   value={formData.type}
//                   onChange={(e) => handleChange("type", e.target.value)}
//                   isInvalid={!!errors.type}
//                 >
//                   {typeOptions.map((option) => (
//                     <option key={option.value} value={option.value}>
//                       {option.label}
//                     </option>
//                   ))}
//                 </Form.Select>
//                 <Form.Control.Feedback type="invalid">
//                   {errors.type}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Percentage (%)</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.percentage}
//                   onChange={(e) =>
//                     handleChange("percentage", formatPercentage(e.target.value))
//                   }
//                   isInvalid={!!errors.percentage}
//                   placeholder="Enter completion percentage (0-100)"
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.percentage}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Status *</Form.Label>
//                 <Form.Select
//                   value={formData.status}
//                   onChange={(e) => handleChange("status", e.target.value)}
//                   isInvalid={!!errors.status}
//                 >
//                   {statusOptions.map((option) => (
//                     <option key={option.value} value={option.value}>
//                       {option.label}
//                     </option>
//                   ))}
//                 </Form.Select>
//                 <Form.Control.Feedback type="invalid">
//                   {errors.status}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Planned Start Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   value={formData.plannedStartDate}
//                   onChange={(e) =>
//                     handleChange("plannedStartDate", e.target.value)
//                   }
//                   max={formData.plannedEndDate || getTodayDate()}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Planned End Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   value={formData.plannedEndDate}
//                   onChange={(e) =>
//                     handleChange("plannedEndDate", e.target.value)
//                   }
//                   min={formData.plannedStartDate}
//                   isInvalid={!!errors.plannedEndDate}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.plannedEndDate}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Actual Start Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   value={formData.actualStartDate}
//                   onChange={(e) =>
//                     handleChange("actualStartDate", e.target.value)
//                   }
//                   max={formData.actualEndDate || getTodayDate()}
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Actual End Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   value={formData.actualEndDate}
//                   onChange={(e) =>
//                     handleChange("actualEndDate", e.target.value)
//                   }
//                   min={formData.actualStartDate}
//                   isInvalid={!!errors.actualEndDate}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.actualEndDate}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Form.Group className="mb-3">
//             <Form.Label>Description</Form.Label>
//             <Form.Control
//               as="textarea"
//               rows={3}
//               value={formData.description}
//               onChange={(e) => handleChange("description", e.target.value)}
//               placeholder="Enter milestone description"
//             />
//           </Form.Group>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Project ID *</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.prozmaPrId}
//                   onChange={(e) => handleChange("prozmaPrId", e.target.value)}
//                   isInvalid={!!errors.prozmaPrId}
//                   disabled={!!projectId}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.prozmaPrId}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Organization ID *</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.orgId}
//                   onChange={(e) => handleChange("orgId", e.target.value)}
//                   isInvalid={!!errors.orgId}
//                   disabled={!!orgId}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.orgId}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={onHide} disabled={loading}>
//             Cancel
//           </Button>
//           <Button variant="primary" type="submit" disabled={loading}>
//             {loading
//               ? "Saving..."
//               : milestone
//               ? "Update Milestone"
//               : "Create Milestone"}
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default MilestoneModal;
